/* @jsx h */
import { defineComponent, h, ref, computed, watch } from "vue";
import { useMenu } from "@app/vue/providers/index";
import { useRoute, useRouter } from "vue-router";
import { createGroupOrder } from "@app/server/client";
import { useLocalization } from "@lemmonhq/uikit";

export const OrderPage = defineComponent({
  name: "OrderPage",
  setup() {
    const { shop } = useMenu();
    const route = useRoute();
    const router = useRouter();
    const { t } = useLocalization();
    const loading = ref(false);
    const error = ref<string | null>(null);

    const shopSlug = route.params.shopslug as string;
    const mode = computed(() => (route.query.mode as string) || "takeaway");

    const handleStartOrder = async () => {
      try {
        loading.value = true;
        error.value = null;
        const groupOrder = await createGroupOrder(shop.value.id);
        router.push(`/restaurant/${shopSlug}/group-order/${groupOrder.id}`);
      } catch (e) {
        error.value = t("customer.reserve.order_create_failed");
        loading.value = false;
      }
    };

    return () => {
      if (!shop.value) return null;

      const isTakeaway = mode.value === "takeaway";
      const title = isTakeaway
        ? t("customer.reserve.tab_takeaway")
        : t("customer.reserve.tab_delivery");
      const subtitle = isTakeaway
        ? t("customer.reserve.takeaway_copy")
        : t("customer.reserve.delivery_copy");
      const icon = isTakeaway ? "shopping_bag" : "delivery_dining";

      return (
        <div class="space-y-6">
          <div class="flex items-center gap-4">
            <div class="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
              <span class="material-symbols-outlined text-2xl text-gray-600">
                {icon}
              </span>
            </div>
            <div>
              <h2 class="text-xl font-medium text-gray-900">{title}</h2>
              <p class="text-sm text-gray-500 font-light">{subtitle}</p>
            </div>
          </div>

          {error.value && (
            <div class="bg-red-50 border border-red-200 rounded-lg p-4">
              <p class="text-red-600 text-sm">{error.value}</p>
            </div>
          )}

          <button
            type="button"
            onClick={handleStartOrder}
            disabled={loading.value}
            class="btn btn-primary btn-lg w-full"
          >
            {loading.value ? (
              <span class="loading loading-spinner loading-sm"></span>
            ) : (
              t("customer.reserve.start_order", [title])
            )}
          </button>
        </div>
      );
    };
  },
});
